import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DeviceType, GroupType, InsuranceClaimType, PolicyType, QuoteType, UserType } from 'sl-sdk';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SharelockApi } from 'api';
import ExternalDashboardLink from 'components/ExternalDashboardLink';
import GroupsSection, { type GroupContext } from 'components/GroupsSection';
import { formatAppVersion, formatDateTime, phoneLookupCandidates, sortClaims, sortPolicies } from 'utils/format';
import { getTicketRequester, getZafClient, REQUESTER_UNAVAILABLE_ERROR, resizeSidebar } from 'zaf/client';

interface Props {
  readonly onLogout: () => void | Promise<void>;
}

// Email is the primary identity; the phone is the fallback for tickets opened by phone.
const findUser = async (email: string | null, phone: string | null): Promise<UserType | null> => {
  if (email) {
    const byEmail = await SharelockApi.users.getUsers(
      `email.equals=${encodeURIComponent(email)}&email.mode=insensitive&_limit=1`
    );
    if (byEmail[0]) {
      return byEmail[0];
    }
  }
  if (phone) {
    for (const candidate of phoneLookupCandidates(phone)) {
      const byPhone = await SharelockApi.users.getUsers(`phone.equals=${encodeURIComponent(candidate)}&_limit=1`);
      if (byPhone[0]) {
        return byPhone[0];
      }
    }
  }
  return null;
};

const firstRejectionReason = (results: PromiseSettledResult<unknown>[]): unknown => {
  const rejected = results.find(result => result.status === 'rejected');
  return rejected?.status === 'rejected' ? rejected.reason : undefined;
};

type GroupsLookup = {
  groups: GroupType[];
  loadError: unknown;
};

// An email or phone can belong to a company as well as to a user, so groups are looked up too.
// Each filter settles on its own so one failing filter does not discard the matches of the others.
const findGroups = async (email: string | null, phone: string | null): Promise<GroupsLookup> => {
  const filters: string[] = [];
  if (email) {
    filters.push(`email.equals=${encodeURIComponent(email)}&email.mode=insensitive&_limit=5`);
  }
  if (phone) {
    for (const candidate of phoneLookupCandidates(phone)) {
      filters.push(`phone.equals=${encodeURIComponent(candidate)}&_limit=5`);
    }
  }
  const results = await Promise.allSettled(filters.map(filter => SharelockApi.groups.getGroups(filter)));
  const byId = new Map<number, GroupType>();
  for (const result of results) {
    if (result.status === 'fulfilled') {
      for (const group of result.value) {
        byId.set(group.id, group);
      }
    }
  }
  return { groups: Array.from(byId.values()), loadError: firstRejectionReason(results) };
};

// A matched company stays visible even when its policies cannot be fetched.
const loadGroupContexts = async (groups: GroupType[]): Promise<GroupContext[]> => {
  const results = await Promise.allSettled(
    groups.map(group => SharelockApi.b2bPolicies.find(`group=${group.id}&_limit=20&_sort=created_at:DESC`))
  );
  return groups.map((group, index) => {
    const result = results[index];
    if (result.status === 'fulfilled') {
      return { group, policies: result.value, policiesLoadFailed: false };
    }
    return { group, policies: [], policiesLoadFailed: true };
  });
};

type UserContext = {
  device: DeviceType | null;
  policies: PolicyType[];
  quotes: QuoteType[];
  claims: InsuranceClaimType[];
};

const loadUserContext = async (found: UserType): Promise<UserContext> => {
  const [devices, userPolicies, userClaims] = await Promise.all([
    SharelockApi.users.getUserDevices(found.id, '_limit=1&_sort=lastUsed:DESC'),
    SharelockApi.policies.getUserPolicies(found.id, '_limit=50&_sort=created_at:DESC&_include=partner_object'),
    SharelockApi.insuranceClaims.getClaims(
      `policy_object.user=${found.id}&_include=policy_object&_limit=50&_sort=created_at:DESC`
    ),
  ]);

  const sortedPolicies = sortPolicies(userPolicies);
  let openQuotes: QuoteType[] = [];
  if (sortedPolicies.length === 0) {
    const quoteFilter = `OR[0].userId=${found.id}&OR[1].client.path[0]=email&OR[1].client.equals=${encodeURIComponent(
      found.email
    )}&status=OPEN&_limit=20&_sort=created_at:DESC`;
    openQuotes = await SharelockApi.quotes.find(quoteFilter);
  }

  return {
    device: devices[0] ?? null,
    policies: sortedPolicies,
    quotes: openQuotes,
    claims: sortClaims(userClaims),
  };
};

type RequesterContext = {
  user: (UserContext & { found: UserType }) | null;
  groupContexts: GroupContext[];
  // First rejection among the lookups, so a failed lookup is never reported as "not found".
  loadError: unknown;
};

const resolveRequesterContext = async (email: string | null, phone: string | null): Promise<RequesterContext> => {
  const [userResult, groupsResult] = await Promise.allSettled([findUser(email, phone), findGroups(email, phone)]);
  const found = userResult.status === 'fulfilled' ? userResult.value : null;
  const groups = groupsResult.status === 'fulfilled' ? groupsResult.value.groups : [];
  const groupsFilterError = groupsResult.status === 'fulfilled' ? groupsResult.value.loadError : undefined;
  const [userContextResult, groupContextsResult] = await Promise.allSettled([
    found ? loadUserContext(found) : null,
    loadGroupContexts(groups),
  ]);
  const userContext = userContextResult.status === 'fulfilled' ? userContextResult.value : null;
  const groupContexts = groupContextsResult.status === 'fulfilled' ? groupContextsResult.value : [];
  return {
    user: found && userContext ? { found, ...userContext } : null,
    groupContexts,
    loadError:
      firstRejectionReason([userResult, groupsResult, userContextResult, groupContextsResult]) ?? groupsFilterError,
  };
};

const SidebarView: React.FC<Props> = ({ onLogout }) => {
  const intl = useIntl();
  const [requesterEmail, setRequesterEmail] = useState<string | null>(null);
  const [requesterPhone, setRequesterPhone] = useState<string | null>(null);
  const [user, setUser] = useState<UserType | null>(null);
  const [device, setDevice] = useState<DeviceType | null>(null);
  const [policies, setPolicies] = useState<PolicyType[]>([]);
  const [quotes, setQuotes] = useState<QuoteType[]>([]);
  const [claims, setClaims] = useState<InsuranceClaimType[]>([]);
  const [groupContexts, setGroupContexts] = useState<GroupContext[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadGenerationRef = useRef(0);

  const toErrorMessage = useCallback(
    (err: unknown): string => {
      if (err instanceof Error && err.message === REQUESTER_UNAVAILABLE_ERROR) {
        return intl.formatMessage({
          id: 'zendesk.sidebar.error.requesterUnavailable',
          defaultMessage: 'Could not load the ticket requester.',
        });
      }
      if (err instanceof Error) {
        return err.message;
      }
      return intl.formatMessage({ id: 'zendesk.sidebar.error.loadFailed', defaultMessage: 'Failed to load context' });
    },
    [intl]
  );

  const load = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    const isCurrent = (): boolean => loadGenerationRef.current === generation;

    setIsLoading(true);
    setError(null);
    setRequesterEmail(null);
    setRequesterPhone(null);
    setUser(null);
    setDevice(null);
    setPolicies([]);
    setQuotes([]);
    setClaims([]);
    setGroupContexts([]);
    try {
      const { email, phone } = await getTicketRequester();
      if (!isCurrent()) {
        return;
      }
      setRequesterEmail(email);
      setRequesterPhone(phone);
      if (!email && !phone) {
        setError(
          intl.formatMessage({
            id: 'zendesk.sidebar.error.noRequester',
            defaultMessage: 'No ticket requester email or phone available.',
          })
        );
        return;
      }

      const context = await resolveRequesterContext(email, phone);
      if (!isCurrent()) {
        return;
      }
      setGroupContexts(context.groupContexts);
      if (context.user) {
        setUser(context.user.found);
        setDevice(context.user.device);
        setPolicies(context.user.policies);
        setClaims(context.user.claims);
        setQuotes(context.user.quotes);
      }
      if (context.loadError !== undefined) {
        setError(toErrorMessage(context.loadError));
      } else if (!context.user && context.groupContexts.length === 0) {
        setError(
          intl.formatMessage(
            { id: 'zendesk.sidebar.error.notFound', defaultMessage: 'No Sharelock user or company for {identity}' },
            { identity: [email, phone].filter(Boolean).join(' / ') }
          )
        );
      }
    } catch (err) {
      if (!isCurrent()) {
        return;
      }
      setError(toErrorMessage(err));
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }, [intl, toErrorMessage]);

  useEffect(() => {
    void load();

    const zaf = getZafClient();
    if (!zaf) {
      return () => {
        loadGenerationRef.current += 1;
      };
    }

    const handleRequesterChanged = (): void => {
      void load();
    };
    // Phone-only requesters can switch without any email change, so watch the id too.
    zaf.on('ticket.requester.email.changed', handleRequesterChanged);
    zaf.on('ticket.requester.id.changed', handleRequesterChanged);
    return () => {
      loadGenerationRef.current += 1;
      zaf.off('ticket.requester.email.changed', handleRequesterChanged);
      zaf.off('ticket.requester.id.changed', handleRequesterChanged);
    };
  }, [load]);

  useEffect(() => {
    void resizeSidebar(isLoading ? '280px' : '640px');
  }, [isLoading, policies.length, quotes.length, claims.length, groupContexts.length]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Sharelock</Typography>
        <Button size="small" onClick={() => void onLogout()}>
          <FormattedMessage defaultMessage="Logout" id="zendesk.sidebar.logout" />
        </Button>
      </Stack>

      {requesterEmail || requesterPhone ? (
        <Typography color="text.secondary" sx={{ mb: 1 }} variant="caption">
          <FormattedMessage
            defaultMessage="Requester: {requester}"
            id="zendesk.sidebar.requester"
            values={{ requester: [requesterEmail, requesterPhone].filter(Boolean).join(' · ') }}
          />
        </Typography>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      {user ? (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline">
              <FormattedMessage defaultMessage="User" id="zendesk.sidebar.section.user" />
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText
                  primary={
                    <ExternalDashboardLink path={`/sl/user/${user.id}`}>
                      {user.firstname} {user.lastname} (#{user.id})
                    </ExternalDashboardLink>
                  }
                  secondary={intl.formatMessage(
                    { id: 'zendesk.sidebar.user.createdAt', defaultMessage: 'Created {date}' },
                    { date: formatDateTime(user.created_at) }
                  )}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary={
                    <ExternalDashboardLink path={`/sl/user/${user.id}/devices`}>
                      <FormattedMessage defaultMessage="Last device activity" id="zendesk.sidebar.user.lastDevice" />
                    </ExternalDashboardLink>
                  }
                  secondary={
                    device
                      ? `${formatDateTime(device.lastUsed)} · ${formatAppVersion(device)} · ${device.deviceName}`
                      : intl.formatMessage({ id: 'zendesk.sidebar.user.noDevice', defaultMessage: 'No device found' })
                  }
                />
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline">
              <FormattedMessage defaultMessage="Policies" id="zendesk.sidebar.section.policies" />
            </Typography>
            {policies.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                <FormattedMessage defaultMessage="No policies" id="zendesk.sidebar.policies.empty" />
              </Typography>
            ) : (
              <List dense disablePadding>
                {policies.map(policy => (
                  <ListItem key={policy.id} disableGutters>
                    <ListItemText
                      primary={
                        <ExternalDashboardLink path={`/sl/policies/${policy.id}`}>
                          #{policy.id} · {policy.policyStatus}
                        </ExternalDashboardLink>
                      }
                      secondary={
                        policy.partner_object ? `${policy.quoteID} · ${policy.partner_object.name}` : policy.quoteID
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {policies.length === 0 ? (
            <Box>
              <Typography variant="overline">
                <FormattedMessage defaultMessage="Quotes" id="zendesk.sidebar.section.quotes" />
              </Typography>
              {quotes.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  <FormattedMessage defaultMessage="No open quotes" id="zendesk.sidebar.quotes.empty" />
                </Typography>
              ) : (
                <List dense disablePadding>
                  {quotes.map(quote =>
                    quote.id != null ? (
                      <ListItem key={quote.id} disableGutters>
                        <ListItemText
                          primary={
                            <ExternalDashboardLink path={`/sl/quotes/${quote.id}`}>
                              #{quote.id} · {quote.status}
                            </ExternalDashboardLink>
                          }
                          secondary={quote.serial || formatDateTime(quote.created_at)}
                        />
                      </ListItem>
                    ) : null
                  )}
                </List>
              )}
            </Box>
          ) : null}

          <Divider />

          <Box>
            <Typography variant="overline">
              <FormattedMessage defaultMessage="Claims" id="zendesk.sidebar.section.claims" />
            </Typography>
            {claims.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                <FormattedMessage defaultMessage="No claims" id="zendesk.sidebar.claims.empty" />
              </Typography>
            ) : (
              <List dense disablePadding>
                {claims.map(claim => (
                  <ListItem key={claim.id} disableGutters>
                    <ListItemText
                      primary={
                        <ExternalDashboardLink path={`/sl/insurance-claims/${claim.id}`}>
                          #{claim.id} · {claim.claimCoverageStatus}
                        </ExternalDashboardLink>
                      }
                      secondary={intl.formatMessage(
                        { id: 'zendesk.sidebar.claims.openedAt', defaultMessage: 'Opened {date}' },
                        { date: formatDateTime(claim.claimOpeningDate) }
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      ) : null}

      <GroupsSection groupContexts={groupContexts} hasUserAbove={Boolean(user)} />

      {user || groupContexts.length > 0 ? (
        <Button size="small" sx={{ mt: 1.5 }} variant="outlined" onClick={() => void load()}>
          <FormattedMessage defaultMessage="Refresh" id="zendesk.sidebar.refresh" />
        </Button>
      ) : null}
    </Box>
  );
};

export default SidebarView;
